<template>
    <div class="contacts-tab row nav-padding">
        <div class="d-flex align-items-center justify-content-between">
            <div class="px-3">
                <div v-if="contacts.length">
                    <span class="_700">Results:</span> {{ contacts.length }}
                </div>
            </div>

            <div class="d-flex justify-content-end px-3 pt-3">
                <div class="mr-2">
                    <company-autosuggest v-model="company_id"></company-autosuggest>
                </div>

                <div class="mr-2">
                    <el-input
                        placeholder="Type something to search"
                        prefix-icon="el-icon-search"
                        v-model="search_text"
                        @clear="contacts = []"
                        @keyup.enter.native="handleSearch"
                        clearable>
                    </el-input>
                </div>

                <div class="mr-2">
                    <el-select v-model="search_field" placeholder="Select a search field">
                        <el-option
                            v-for="item in search_field_options"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value">
                        </el-option>
                    </el-select>
                </div>

                <div>
                    <el-button
                        v-if="showClearFiltersButton"
                        @click="clearFilters()">
                        Reset Filters
                    </el-button>

                    <el-button
                        type="success"
                        @click="handleSearch"
                        :disabled="searching">
                        Search
                    </el-button>
                </div>
            </div>
        </div>

        <div class="w-100 pt-5">
            <div
                v-if="contacts.length"
                class="p-3">
                <el-table
                    :data="contacts"
                    style="width: 100%">
                    <el-table-column label="ID">
                        <template v-slot="scope">
                            <span class="mb-0" v-bind:class="highlight(scope.row.id)">{{ scope.row.id }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="Company Name">
                        <template v-slot="scope">
                            <p class="mb-0">{{ scope.row.company_name }}</p>
                            <p class="mb-0">#{{ scope.row.company_id }}</p>
                        </template>
                    </el-table-column>
                    <el-table-column
                        prop="name"
                        label="Name">
                    </el-table-column>
                    <el-table-column label="Primary Phone Number">
                        <template v-slot="scope">
                            <span v-bind:class="highlight(scope.row.phone_number)">{{ scope.row.phone_number }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="Phone Numbers">
                        <template v-slot="scope">
                            <div v-for="phone_number in scope.row.phone_numbers"
                                 :key="phone_number.id"
                                 class="mb-0">
                                <span v-bind:class="highlight(phone_number.phone_number)">{{ phone_number.phone_number }}</span>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Operations">
                        <template v-slot="scope">
                            <div>
                                <div class="mb-1">
                                    <el-button
                                        size="mini"
                                        type="primary"
                                        @click="redirect(`/admin/company/${scope.row.company_id}`)">
                                        Open Card
                                    </el-button>
                                </div>

                                <div>
                                    <el-button
                                        v-if="scope.row.is_dnc"
                                        size="mini"
                                        type="danger"
                                        :disabled="updating_dnc_status"
                                        @click="confirm(scope.$index, scope.row, 'remove')">Un-DNC
                                    </el-button>

                                    <el-button
                                        v-else
                                        size="mini"
                                        type="success"
                                        :disabled="updating_dnc_status"
                                        @click="confirm(scope.$index, scope.row, 'add')">DNC
                                    </el-button>
                                </div>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>
            </div>

            <div v-else
                 class="h-100 d-flex align-items-center">
                <div v-if="searching"
                     class="w-100">
                    <h3 class="text-center">Loading...</h3>
                </div>

                <div v-else-if="no_results"
                     class="w-100">
                    <h3 class="text-center">No results.</h3>
                </div>

                <div v-else
                     class="w-100 search-resource-text">
                    <div class="text-center">
                        <i class="fa fa-search"></i>
                        <p class="mb-0">Search contacts</p>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog
            title="Confirm"
            :visible.sync="dialog_visible"
            width="30%">
            <div>
                <p>{{ confirm_message }}</p>
                <div>
                    <span>Please enter a reason:</span>
                </div>
                <el-input v-model="reason"></el-input>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="handleDialogHide">Cancel</el-button>
                <el-button type="success"
                           @click="toggleDNCStatus"
                           :disabled="!reason || updating_dnc_status">
                    Confirm
                </el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import CompanySelector from '../company-selector'
import CompanyAutosuggest from '../company-autosuggest'

export default {
    data() {
        return {
            contacts: [],
            last_search_text: '',
            search_text: '',
            updating_dnc_status: false,
            searching: false,
            company_id: null,
            search_field: 'phone_number',
            search_field_options: [
                {
                    value: 'id',
                    label: 'ID'
                },
                {
                    value: 'phone_number',
                    label: 'Phone Number'
                }
            ],
            dialog_visible: false,
            reason: '',
            contact_to_toggle: {
                id: null,
                index: null,
                row: null,
                action: null
            },
            confirm_message: '',
            no_results: false
        }
    },

    components: {
        CompanySelector,
        CompanyAutosuggest
    },

    computed: {
        showClearFiltersButton() {
            return this.company_id || this.search_text || this.search_field != 'phone_number'
        }
    },

    methods: {
        clearFilters() {
            this.company_id = null
            this.search_text = ''
            this.search_field = 'phone_number'
            this.no_results = false
        },

        confirm(index, row, action) {
            this.dialog_visible = true
            const contact = this.contacts[index]
            this.contact_to_toggle = {
                id: contact.id,
                index,
                row,
                action
            }
            this.confirm_message = `Are you sure you want to ${action == 'add' ? 'DNC' : 'Un-DNC'} ${contact.name}?`
        },

        toggleDNCStatus() {
            const {reason} = this

            if (!reason) {
                return this.$notify.error({
                    title: 'Error',
                    message: 'Please enter a reason'
                })
            }

            this.updating_dnc_status = true

            axios.patch(`/api/admin/contacts/${this.contact_to_toggle.id}/dnc?should_dnc=${this.contact_to_toggle.action == 'add' ? 1 : 0}`, {
                reason
            })
                .then(response => {
                    const {data} = response
                    let i = this.contacts.findIndex(contact => contact.id == data.id)

                    if (i > -1) {
                        this.contacts[i].is_dnc = data.is_dnc

                        this.$notify.success({
                            title: 'Success',
                            message: `The DNC status for the contact with ID ${data.id} has been updated.`
                        })

                        // reset dialog data
                        this.contact_to_toggle = {
                            id: null,
                            index: null,
                            row: null,
                            action: null
                        }
                        this.confirm_message = ''
                        this.reason = ''
                        this.dialog_visible = false
                    }
                })
                .catch(error => {
                    console.error({
                        error
                    })

                    this.$notify.error({
                        title: 'Error',
                        message: error.message
                    })
                })
                .finally(() => this.updating_dnc_status = false)
        },

        handleSearch() {
            if (!this.search_text) {
                return this.$notify.error({
                    title: 'Error',
                    message: 'Please enter a contact id or phone number before searching'
                })
            }

            if (!this.search_field) {
                return this.$notify.error({
                    title: 'Error',
                    message: 'Please select a search field'
                })
            }

            if (this.search_field == 'phone_number' && !(this.search_text.length >= 10)) {
                return this.$notify.error({
                    title: 'Error',
                    message: 'Please enter a 10 digit phone number'
                })
            }

            this.searching = true
            this.last_search_text = this.search_text

            let url = `/api/admin/contacts/search?search_text=${this.search_text}&search_field=${this.search_field}`

            if (this.company_id) {
                url += `&company_id=${this.company_id}`
            }

            axios.get(url)
                .then(response => {
                    const {data: contacts} = response

                    if (contacts.length == 0) {
                        this.no_results = true
                    } else {
                        this.no_results = false
                    }

                    this.contacts = contacts
                })
                .catch(error => {
                    this.$notify.error({
                        title: 'Error',
                        message: error.message
                    })
                })
                .finally(() => this.searching = false)
        },

        handleCompanyChange(company_id) {
            this.company_id = company_id
        },

        handleDialogHide() {
            this.dialog_visible = false
            this.reason = ''
        },

        highlight(value) {
            let classes = [],
                search_text = this.last_search_text.replace(/[- )(]/g, '')

            if (value.toString() == search_text || value.toString().includes(search_text)) {
                classes.push('highlight')
            }

            return classes
        },

        redirect(path) {
            this.$router.push({path})
        }
    }
}
</script>
